import CaseStatusCheckboxes from "@/components/CaseStatusCheckboxes.tsx";
import CoreRolesCheckboxes from "@/components/CoreRolesCheckboxes.tsx";
import { tags } from "@/components/Forms/AddNoteDialog.tsx";
import { addTaskTemplateSchema } from "@/components/Forms/schema/taskTemplateSchema.ts";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useAddTaskTemplate } from "@/mutations/useAddTaskTemplate.tsx";
import { useFetchTaskTemplate } from "@/queries/useFetchTaskTemplate.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
    MenuItem,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export type AddTaskTemplateFormValues = z.infer<typeof addTaskTemplateSchema>;

type Option = {
    days: string;
    name: string;
};

export const durationUnitOptions: Option[] = [
    {
        days: "days",
        name: "Days",
    },
    {
        days: "weeks",
        name: "Weeks",
    },
    {
        days: "months",
        name: "Months",
    },
];

const getResetValuesFromData = (
    data: Partial<AddTaskTemplateFormValues>,
): AddTaskTemplateFormValues => ({
    name: data.name ?? "",
    description: data.description ?? "",
    duration: String(data.duration ?? "1"),
    durationUnit: data.durationUnit ?? "",
    defaultNoteTag: data.defaultNoteTag ?? "",
    coreRoles: data.coreRoles && data.coreRoles.length > 0 ? data.coreRoles : ["AllRoles"],
    caseStatuses: data.caseStatuses && data.caseStatuses.length > 0 ? data.caseStatuses : ["Open"],
});

type Props = {
    showAddButton?: boolean;
    editTaskTemplateId?: string;
    clearEditTaskTemplateId?: () => void;
};

const AddEditTaskTemplateDialog = ({
    showAddButton = true,
    editTaskTemplateId,
    clearEditTaskTemplateId,
}: Props): ReactNode => {
    const { hasPermission } = useEmployee();
    const addTaskTemplate = useAddTaskTemplate(editTaskTemplateId);
    const [open, setOpen] = useState(false);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchTaskTemplate({
        taskTemplateUUID: editTaskTemplateId ?? null,
        isQueryEnabled: editTaskTemplateId !== undefined,
    });

    const form = useForm<AddTaskTemplateFormValues>({
        resolver: zodResolver(addTaskTemplateSchema),
        defaultValues: {
            coreRoles: ["AllRoles"],
        },
    });

    const handleSubmit = (values: AddTaskTemplateFormValues) => {
        addTaskTemplate
            .mutateAsync({
                ...values,
            })
            .then(() => {
                if (editTaskTemplateId) {
                    enqueueSnackbar("Task Template updated.", { variant: "success" });
                } else {
                    enqueueSnackbar("Task Template added.", { variant: "success" });
                }

                handleClose();
            })
            .catch(() => {
                if (editTaskTemplateId) {
                    enqueueSnackbar("Error: Edit Task Template failed.", { variant: "error" });
                    return;
                }

                enqueueSnackbar("Error: Add Task Template failed.", { variant: "error" });
            });
    };

    const handleClose = () => {
        if (open) {
            setOpen(false);
        }

        form.reset({
            name: "",
            description: "",
            duration: "1",
            durationUnit: "",
            defaultNoteTag: "",
        });

        if (clearEditTaskTemplateId) {
            clearEditTaskTemplateId();
        }
    };

    const onSubmit: SubmitHandler<AddTaskTemplateFormValues> = async (
        data: AddTaskTemplateFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    useEffect(() => {
        if (editTaskTemplateId && data) {
            const resetValues = getResetValuesFromData(
                data as unknown as Partial<AddTaskTemplateFormValues>,
            );
            form.reset(resetValues);

            if (!open) {
                setOpen(true);
            }

            return;
        }

        if (!open) {
            setOpen(false);
        }
    }, [data, editTaskTemplateId]);

    useEffect(() => {
        if (open && !editTaskTemplateId) {
            form.reset({
                name: "",
                description: "",
                duration: "1",
                durationUnit: "",
                defaultNoteTag: "",
                coreRoles: ["AllRoles"],
            });
        }
    }, [open]);

    if (!hasPermission("create:task-template")) {
        return null;
    }

    return (
        <>
            {showAddButton && (
                <Button
                    variant={"contained"}
                    sx={{
                        ml: 2,
                        minWidth: "120px",
                    }}
                    onClick={() => setOpen(true)}
                >
                    New Task Template
                </Button>
            )}
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>
                            {editTaskTemplateId ? "Edit" : "New"} Task Template
                        </DialogTitle>
                        <DialogContent dividers>
                            {(isLoading || isRefetching) && <CircularProgress />}
                            {isError && <Alert severity={"error"}>Error loading form data.</Alert>}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"name"}
                                        label={"Task Template Name"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 2 }}>
                                    <RhfTextField
                                        control={form.control}
                                        name="duration"
                                        label="Duration"
                                        type="text"
                                        slotProps={{
                                            input: {
                                                inputMode: "numeric",
                                            },
                                            formHelperText: {
                                                children: "Set to 0 for no due date",
                                            },
                                        }}
                                        sx={{ width: "100%" }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"durationUnit"}
                                        label={"Duration Units"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    >
                                        {durationUnitOptions.map((durationOption) => (
                                            <MenuItem
                                                key={`duration-${durationOption.days}`}
                                                value={durationOption.days}
                                            >
                                                {durationOption.name}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"description"}
                                        label={"Task Template Description"}
                                        sx={{
                                            width: "100%",
                                        }}
                                        multiline
                                        rows={4}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <RhfTextField
                                        select
                                        control={form.control}
                                        name={"defaultNoteTag"}
                                        label={"Default Note Tag"}
                                        sx={{
                                            width: "100%",
                                            mb: 2,
                                            mt: 1,
                                        }}
                                    >
                                        {tags.map((tag) => (
                                            <MenuItem key={tag} value={tag}>
                                                {tag}
                                            </MenuItem>
                                        ))}
                                    </RhfTextField>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <CoreRolesCheckboxes />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <CaseStatusCheckboxes />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => handleClose()}>Close</Button>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                {editTaskTemplateId ? "Edit" : "Add"} Task Template
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default AddEditTaskTemplateDialog;
